import $ from 'jquery';
import 'jquery.ui.widget';
import 'jquery.ui.mouse';
import 'jquery.ui.core';
import 'jquery.ui.draggable';
import 'jquery.ui.droppable';
import 'jquery.ui.sortable';
import 'jqueryfileupload';
// Above jquery import order is specific - don't change without verifying.

import AlerterView from 'chairisher/view/alerter';
import CookieUtils, { CookieName } from 'chairisher/util/cookie';
import createDomElement from 'chairisher/util/dom';
import notifyHoneybadger from 'chairisher/thirdparty/honeybadger';

import { VALID_MIME_TYPES } from 'chairisher/util/image';
import { isContentTooLarge } from 'chairisher/util/status';

/**
 * A component that allows users to upload one or multiple images asynchronously.
 *
 * @param {Object=} settings Object of settings used to customize the ImageField instance
 * @param {string=} settings.actionDeleteSelector Selector for the delete control
 * @param {string=} settings.actionDownloadSelector Selector for the download control
 * @param {string=} settings.actionViewSelector Selector for the "open in new tab" control
 * @param {string=} settings.captionSelector Selector for the caption area
 * @param {string=} settings.endpointUrl URL used to fetch an image and to POST new uploads
 * @param {Array<Object>=} settings.formData Key/value pairs merged into every upload request
 * @param {string=} settings.iconUploadSelector Selector for the upload affordance
 * @param {string=} settings.imageControlSelector Selector for each image control (used in multi-image layouts)
 * @param {ImageData=} settings.imageData Image payload when the field initially shows an image, otherwise null
 * @param {string=} settings.imageSelector Selector for the image preview container
 * @param {boolean=} settings.isSingleImageUploader True: new upload replaces the current image. False: one image per slot (multi-slot UI)
 * @param {string=} settings.progressBarSelector Selector for the upload progress bar
 * @param {jQuery=} settings.$el Root element for this ImageField
 */
class ImageField {
    constructor(settings) {
        const options = $.extend(
            {
                actionDeleteSelector: '.js-action-delete',
                actionDownloadSelector: '.js-action-download',
                actionViewSelector: '.js-action-view',
                captionSelector: '.js-caption',
                endpointUrl: '',
                formData: [],
                iconUploadSelector: '.js-cicon-upload',
                imageControlSelector: '.js-image-control',
                imageData: null,
                imageSelector: '.js-image',
                progressBarSelector: '.js-progress-bar',
                $el: null,
            },
            settings,
        );

        this.actionDeleteSelector = options.actionDeleteSelector;
        this.actionDownloadSelector = options.actionDownloadSelector;
        this.actionViewSelector = options.actionViewSelector;
        this.captionSelector = options.captionSelector;
        this.endpointUrl = options.endpointUrl;
        this.formData = options.formData;
        this.iconUploadSelector = options.iconUploadSelector;
        this.imageControlSelector = options.imageControlSelector;
        this.imageSelector = options.imageSelector;
        this.isSingleImageUploader = options.isSingleImageUploader;
        this.progressBarSelector = options.progressBarSelector;

        this.imageData = options.imageData;

        this.$el = options.$el;

        this.actionInfoSelector = '.js-action-info';
        this.hasImageClass = 'has-image';
        this.imageSkeletonSelector = '.js-image-skeleton';
        this.productImageEmptySlotClass = 'product-image-empty-slot';
        this.productImageControlWrapperSelector = '.js-product-image-control-wrapper';
        this.isEnabled = true;
        this.productImageClass = 'product-image-container';
    }

    disable() {
        this.isEnabled = false;
        this.$el.addClass('disabled');
        this.$el.find('input').prop('disabled', true);
    }

    /**
     * Adds an image to the ImageField (renders from `imageData`).
     */
    displayImage() {
        if (this.hasImage()) {
            const imageGuid = this.imageData.value;
            let imageUrl = this.imageData.display;

            if (this.imageData.getParams) {
                imageUrl += `?${$.param(this.imageData.getParams)}`;
            } else if (imageUrl.indexOf('sized') !== -1) {
                imageUrl += '?aspect=fit&size=800';
            }

            const $el = this.getEl();

            $el.find(this.imageSkeletonSelector).removeClass('hidden');

            const thumbId = `product-image-thumb-${imageGuid}`;

            $el.find(this.imageSelector).html(
                $('<img />', {
                    id: thumbId,
                    src: imageUrl,
                }).on('load', () => {
                    $el.find(this.imageSkeletonSelector).addClass('hidden');
                    $el.trigger('imagefield:imageloaded');
                }),
            );

            const downloadUrl = this.imageData.master_url || imageUrl;

            $el.find(this.actionDownloadSelector).attr({
                download: downloadUrl,
                href: downloadUrl,
            });

            $el.find(this.actionViewSelector).attr({
                href: imageUrl,
            });

            const { info } = this.imageData;
            const $infoAction = $el.find(this.actionInfoSelector);
            $infoAction.toggleClass('hidden', !info);

            if (info) {
                $infoAction.off('click').on('click', (e) => {
                    e.preventDefault();
                    AlerterView.info(info);
                });
            }

            this.hideCaption();
            this.hideUploadIcon();
        }
    }

    enable() {
        this.isEnabled = true;
        this.$el.removeClass('disabled');
        this.$el.find('input').prop('disabled', false);
    }

    /**
     * @returns {jQuery} The jQuery instance representing the ImageField
     */
    getEl() {
        return this.$el;
    }

    /**
     * @returns {Object} Object containing image data
     */
    getImageData() {
        return this.imageData;
    }

    /**
     * @returns {jQuery} The element that contains the image
     */
    getImageContainerEl() {
        return this.$el.find(this.imageSelector);
    }

    /**
     * @returns {jQuery} The jQuery instance representing the image tag
     */
    getImageEl() {
        return this.getImageContainerEl().find('img');
    }

    /**
     * @returns {boolean} true indicates the ImageField contains an image, false otherwise
     */
    hasImage() {
        return this.imageData !== null;
    }

    /**
     * Removes the current image and triggers the `ImageField:imageremoved` event for clients to use as a hook
     *
     * @param {boolean=} shouldTriggerEvent True indicates the `imagefield:imageremoved` event should be triggered
     */
    removeImage(shouldTriggerEvent) {
        this.getEl().find(this.imageSelector).html('');

        this.setImageData(null);
        this.showCaption();
        this.showUploadIcon();

        if (shouldTriggerEvent === true) {
            this.trigger('imagefield:imageremoved');
        }
    }

    /**
     * Adds the "Primary" label to the ImageField's wrapper element.
     */
    addPrimaryLabel() {
        const label = createDomElement('span', {
            classes: ['js-primary-image-label', 'primary-image-label'],
        });
        label.textContent = 'Primary';
        this.$el.find('.js-image-wrapper')[0].appendChild(label);
    }

    /**
     * Removes the "Primary" label from the ImageField's wrapper element.
     */
    removePrimaryLabel() {
        const label = this.$el.find('.js-primary-image-label')[0];
        if (label) {
            label.parentNode.removeChild(label);
        }
    }

    /**
     * @param {Object} imageData Object containing image data
     * @param {boolean=} shouldTriggerEvent True indicates `imagefield:imagedatachanged` event should be triggered
     */
    setImageData(imageData, shouldTriggerEvent) {
        this.imageData = imageData;
        this.$el.toggleClass(this.hasImageClass, this.hasImage());

        const wrapper = document.querySelector(this.productImageControlWrapperSelector);

        if (wrapper) {
            const controls = Array.from(wrapper.querySelectorAll(this.imageControlSelector));
            const hasImageMap = controls.map((el) => el.classList.contains(this.hasImageClass));

            wrapper.classList.toggle('has-any-image', hasImageMap.some(Boolean));

            controls.forEach((el, i) => {
                const hasImage = hasImageMap[i];
                el.classList.toggle(this.productImageClass, hasImage);
                el.classList.toggle('hidden', !hasImage);
                el.classList.remove(this.productImageEmptySlotClass);
            });
            this.showUploaderSlot(wrapper, controls);
        }

        if (shouldTriggerEvent === true) {
            this.trigger('imagefield:imagedatachanged', this.imageData);
        }
    }

    /**
     * Binds actions to the various elements that comprise the ImageField
     */
    bind() {
        if (!$.support.xhrFileUpload) {
            this.formData.push({
                name: 'csrfmiddlewaretoken',
                value: CookieUtils.get(CookieName.CSRF_TOKEN),
            });
        }

        const $el = this.getEl();

        // bind the file upload jQuery plugin to `this.$el`...
        $el.fileupload({
            dataType: 'json', // the type of data to get back from the server
            dropZone: $el, // the jQuery object where images can be dragged and dropped
            formData: this.formData, // the array of data to add files to
            paramName: 'image', // the name of the parameter to use when sending data to the server
            pasteZone: $el, // the jQuery object where images can be copied and pasted (chrome only)
            type: 'POST', // the HTTP method to use when sending data to the server
            url: this.endpointUrl, // the URL to send data to
        });

        // bind clicking the delete button...
        $el.on('click', this.actionDeleteSelector, (e) => {
            e.preventDefault();
            if (this.isEnabled) {
                this.removeImage(true);
            }
        });

        // bind behavior for when files are uploaded either via clicking into the
        // widget and revealing the system finder, or by dragging and dropping...
        $el.on('fileuploadadd', (event, data) => {
            if (this.hasImage() && !this.isSingleImageUploader) {
                event.preventDefault();
                event.stopPropagation();
            } else if (this.isEnabled && data.files) {
                this.removeImage();
                this.toggleError(false);
                this.trigger('imagefield:imageuploadadd');
                data.process().done(() => {
                    data.submit();
                });
            }
        });

        // bind behavior to update the ui when files are dragged onto a dropzone...
        $el.on('fileuploaddragover', (e) => {
            const { delegatedEvent } = e;

            // prevent the drag event from bubbling up to the
            // document; the effect will be disabled...
            delegatedEvent.stopPropagation();

            if (this.eventContainsImage(delegatedEvent) && !this.hasImage()) {
                delegatedEvent.dataTransfer.effectAllowed = 'all';
                delegatedEvent.dataTransfer.dropEffect = 'copy';
            }
        });

        // bind behavior for when the file is done being uploaded...
        $el.on('fileuploaddone', (e, data) => {
            this.hideProgressBar();

            if (data.result.error) {
                data.fail(e, data);
                return;
            }

            // if this Imagefield has an image don't overwrite it;
            // let clients handle the imagefield.imagenotadded event instead
            if (this.hasImage()) {
                this.trigger('imagefield:imagenotadded', data.result);
            } else {
                this.setImageData(data.result);
                this.displayImage();
                this.trigger('imagefield:imageadded', data.result);
                this.trigger('imagefield:imagedatachanged', this.imageData);
            }
        });

        // bind behavior for when the upload is unsuccessful...
        $el.on('fileuploadfail', (event, data) => {
            this.hideProgressBar();

            if (!this.hasImage()) {
                this.showUploadIcon();
                this.showCaption();
                this.toggleError(true);
            }

            let message = 'Please upload a valid .jpg, .gif or .png image.';
            if (data?.jqXHR?.responseJSON?.errors) {
                message = data.jqXHR.responseJSON.errors[0].message;
            } else if (isContentTooLarge(data?.jqXHR?.status)) {
                message = 'Image is too large. Please upload a smaller image';
            }

            // FIXME: CHAIR-18608 Remove once image slowness issues are determined
            notifyHoneybadger({ message, context: { data, event } });

            this.trigger('imagefield:imageuploadfail', { errors: [{ display: message }] });
        });

        // bind behavior to update the ui for when the server provides progress...
        $el.on('fileuploadprogressall', (event, data) => {
            this.updateProgress(Math.round((data.loaded / data.total) * 100));
        });

        // bind behavior to cancel send if the ImageField already has an image...
        $el.on('fileuploadsend', () => !this.hasImage());

        // bind behavior for when we begin uploading...
        $el.on('fileuploadstart', () => {
            this.hideUploadIcon();
            this.showProgressBar();
        });

        $el.on('click', 'input[type="file"]', (e) => {
            if (this.hasImage() && !this.isSingleImageUploader) {
                e.preventDefault();
                e.stopPropagation();
            }
        });

        $el.on('click', this.actionDownloadSelector, (e) => {
            e.stopPropagation(); // don't let the event bubble and trigger a click on the image element...
        });
    }

    /**
     * Helper function to determine if contains an image file.
     *
     * @param {Event} e The event object, implementing the DragEvent interface.
     * @returns {boolean} True if the first dragged item is an image file.
     */
    eventContainsImage(e) {
        let hasImage = false;

        if (e.dataTransfer) {
            const { files } = e.dataTransfer;
            hasImage = files.length && $.inArray(files[0].type, VALID_MIME_TYPES) !== -1;
        }

        return hasImage;
    }

    /**
     * Hides the ImageField's caption element.
     */
    hideCaption() {
        this.toggleCaption(false);
    }

    /**
     * Hides the ImageField's progress bar element.
     */
    hideProgressBar() {
        this.toggleProgressBar(false);
    }

    /**
     * Hides the ImageField's upload icon element.
     */
    hideUploadIcon() {
        this.toggleUploadIcon(false);
    }

    /**
     * Shows the ImageField's caption element.
     */
    showCaption() {
        this.toggleCaption(true);
    }

    /**
     * Shows the ImageField's progress bar element.
     */
    showProgressBar() {
        this.toggleProgressBar(true);
    }

    /**
     * Shows the ImageField's icon element.
     */
    showUploadIcon() {
        this.toggleUploadIcon(true);
    }

    /**
     * Helper method to toggle visibility of an element based on optional parameter.
     *
     * @param {jQuery} $element The jQuery element to toggle
     * @param {boolean=} shouldBeVisible Optional boolean that indicates if the element should be visible. Defaults to false.
     */
    toggleElementVisibility($element, shouldBeVisible = false) {
        $element.toggleClass('hidden', !shouldBeVisible);
    }

    /**
     * Toggles the visibility of the ImageField's caption element.
     *
     * @param {boolean=} shouldBeVisible Optional boolean that indicates if the caption should be visible. Defaults to false.
     */
    toggleCaption(shouldBeVisible) {
        this.toggleElementVisibility(this.getEl().find(this.captionSelector), shouldBeVisible);
    }

    /**
     * Toggles the error state for this field.
     *
     * @param {boolean} shouldAddError True indicates the field should display the error state, false otherwise
     */
    toggleError(shouldAddError) {
        this.$el.toggleClass('has-error', shouldAddError);
    }

    /**
     * Toggles the visibility of the ImageField's progress bar element.
     *
     * @param {boolean=} shouldBeVisible Optional boolean that indicates if the progress bar  should be visible. Defaults to false.
     */
    toggleProgressBar(shouldBeVisible) {
        this.toggleElementVisibility(this.getEl().find(this.progressBarSelector).parent(), shouldBeVisible);
    }

    /**
     * Toggles the visibility of the ImageField's upload icon element.
     *
     * @param {boolean=} shouldBeVisible Optional boolean that indicates if the icon should be visible. Defaults to false.
     */
    toggleUploadIcon(shouldBeVisible) {
        this.toggleElementVisibility(this.getEl().find(this.iconUploadSelector), shouldBeVisible);
    }

    /**
     * Triggers a new event while passing along any other additional data provided
     *
     * @param {string} type The new event's type
     * @param {Object=} data Additional data to be passed along as an argument to the event handler
     */
    trigger(type, data) {
        this.getEl().trigger($.Event(type), data);
    }

    /**
     * Updates the ui with the current progress passed back from the server
     *
     * @param {number} progressPercent The percent (0-100) to indicate
     */
    updateProgress(progressPercent) {
        const $progressBar = this.getEl().find(this.progressBarSelector);
        $progressBar.css('width', `${progressPercent}%`);

        $progressBar.parent().toggleClass('progress-striped active', progressPercent >= 100);
    }

    /**
     * Ensures only one empty slot is visible as the uploader slot, and moves it to the end.
     * If there are no filled slots, removes the product image class from the uploader slot.
     *
     * @param {HTMLElement} wrapper
     * @param {HTMLElement[]} controls
     */
    showUploaderSlot(wrapper, controls) {
        const empties = controls.filter((el) => !el.classList.contains(this.hasImageClass));

        empties.forEach((el) => el.classList.remove(this.productImageEmptySlotClass));

        if (empties.length) {
            const firstEmpty = empties[0];

            firstEmpty.classList.remove('hidden');
            firstEmpty.classList.add(this.productImageEmptySlotClass);

            firstEmpty.classList.toggle(
                this.productImageClass,
                controls.some((el) => el.classList.contains(this.hasImageClass)),
            );

            if ([...wrapper.children].indexOf(firstEmpty) !== controls.length - 1) {
                wrapper.appendChild(firstEmpty);
            }
        }
    }
}

export default ImageField;
